import React, { useEffect, useState } from 'react';
import { Star, Check, Plus } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { GetProfilewithWorks, setProfileWithWork } from '../../Redux/Slices/Pages/UserProfile/ProfileSlice';
import { useParams } from 'react-router-dom';

const ServiceProviderProfile = () => {
  const dispatch = useDispatch();
  const { workerId } = useParams();

  const profileInfo = useSelector((state) => state.profile.getprofileWithWork);
  const [workerData, setWorkerData] = useState({});
  const [serviceData, setServiceData] = useState([]);
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    if (profileInfo.worker) {
      setWorkerData(profileInfo.worker);
    }
    if (profileInfo.services) {
      setServiceData(profileInfo.services);
    }
  }, [profileInfo]);

  useEffect(() => {
    if (workerId) {
      dispatch(GetProfilewithWorks(workerId));
    }
  }, [dispatch, workerId]);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${
          index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  // Early return if no data loaded yet
  if (!serviceData.length || !workerData.workerName) {
    return (
      <div className="mx-5 mt-5 bg-white min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const currentService = serviceData[activeService];

  return (
    <div className="mx-5 mt-5 bg-white min-h-screen">
      {/* Profile Section */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <img
                src={currentService?.profileImage || "/api/placeholder/80/80"}
                alt={workerData?.workerName}
                className="w-20 h-20 rounded-full object-cover"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-gray-900">{workerData?.workerName}</h1>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-blue-500" />
                  <span className="text-blue-500 text-sm font-medium">Verified</span>
                </div>
              </div>
              <p className="text-gray-600 mb-1">{currentService?.profession}</p>
              <p className="text-gray-500">{currentService?.baseLocation}</p>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-1">
              {renderStars(Math.round(currentService?.avgRating || 0))}
              <span className="ml-2 text-sm text-gray-600">
                ({currentService?.avgRating || 0})
              </span>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
              <span>Add profession</span>
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Service Type Tabs */}
        <div className="flex gap-4 p-6 bg-gray-50 mb-6">
          {serviceData.map((service, index) => (
            <button
              key={service._id}
              onClick={() => setActiveService(index)}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeService === index
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {service.profession}
            </button>
          ))}
        </div>

        {/* Service Category Badge */}
        <div className="mb-6">
          <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            {currentService?.category}
          </span>
        </div>

        {/* Pricing Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-600 text-sm mb-1">Service Charge:</p>
            <p className="text-xl font-bold text-gray-900">
              {currentService?.hourlyRate ? `₹${currentService.hourlyRate} /hour` : 'Contact for pricing'}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-600 text-sm mb-1">Visiting Charge:</p>
            <p className="text-xl font-bold text-gray-900">₹{currentService?.visitingFee || 0}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-600 text-sm mb-1">Travel Charge:</p>
            <p className="text-xl font-bold text-gray-900">₹{currentService?.travelCharge || 0}/km after 5 km</p>
          </div>
        </div>

        {/* Description */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">About this service</h3>
          <p className="text-gray-700 leading-relaxed">
            {currentService?.description || 'No description available for this service.'}
          </p>
        </div>

        {/* Skills/Service Tags */}
        <div className="mb-12">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills & Specializations</h3>
          <div className="flex flex-wrap gap-3">
            {currentService?.skills?.map((skill, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Service Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{currentService?.works?.length || 0}</div>
            <div className="text-gray-600">Completed Jobs</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{currentService?.reviews?.length || 0}</div>
            <div className="text-gray-600">Reviews</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{currentService?.avgRating || 0}</div>
            <div className="text-gray-600">Average Rating</div>
          </div>
        </div>

        {/* Completed Services Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Completed Services</h2>
          {currentService?.works?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {currentService.works.map((work, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                    <img
                      src={work.image || "/api/placeholder/300/200"}
                      alt={`Completed service ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="absolute bottom-3 left-3 bg-white bg-opacity-90 backdrop-blur-sm px-3 py-1 rounded-full flex gap-1">
                    {renderStars(work.rating || 5)}
                  </div>
                  {work.title && (
                    <div className="mt-2">
                      <p className="font-medium text-gray-900">{work.title}</p>
                      {work.description && (
                        <p className="text-sm text-gray-600">{work.description}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>No completed services to display yet.</p>
            </div>
          )}
        </div>

        {/* Reviews Section */}
        {currentService?.reviews?.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Reviews</h2>
            <div className="space-y-4">
              {currentService.reviews.slice(0, 3).map((review, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    {renderStars(review.rating || 5)}
                    <span className="text-sm text-gray-600">
                      {review.customerName || 'Anonymous'}
                    </span>
                  </div>
                  <p className="text-gray-700">{review.comment}</p>
                  {review.date && (
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(review.date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Book Service Button */}
        <div className="fixed bottom-6 right-6">
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold shadow-lg transition-colors">
            Book Service
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceProviderProfile;